<template>
  <div class="item-price-statistics">
    <div class="header-section">
      <div class="header-content">
        <div class="header-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="1" x2="12" y2="23"></line>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
          </svg>
        </div>
        <div>
          <h2>物价统计</h2>
          <p>查询道具在各大区的价格信息</p>
        </div>
      </div>
    </div>

    <!-- 搜索区域 -->
    <div class="search-section">
      <div class="search-wrapper">
        <el-autocomplete
          v-model="searchKeyword"
          :fetch-suggestions="searchItems"
          placeholder="🔍 请输入道具名称搜索（24小时内数据）"
          clearable
          @select="handleItemSelect"
          @clear="handleItemClear"
          class="custom-autocomplete"
        >
          <template #default="{ item }">
            <div class="autocomplete-item">{{ item.value }}</div>
          </template>
        </el-autocomplete>
      </div>
    </div>

    <!-- 价格列表 -->
    <div v-if="selectedItemName && priceList.length > 0" class="price-section">
      <div class="section-header">
        <div class="item-badge">{{ selectedItemName }}</div>
        <span class="item-description">各大区价格 · 24小时内最新 · 按价格降序</span>
      </div>
      
      <div class="price-grid">
        <div 
          v-for="item in paginatedPriceList" 
          :key="item.region"
          class="price-card"
          @click="handleRowClick(item)"
        >
          <div class="card-header">
            <span class="region-name">{{ item.region }}</span>
            <svg class="arrow-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
          <div class="price-value">{{ formatPrice(item.price) }}</div>
          <div class="update-time">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            {{ formatDateTime(item.lastUpdateTime) }}
          </div>
        </div>
      </div>
      
      <!-- 分页 -->
      <div v-if="priceList.length > pageSize" class="pagination-wrapper">
        <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="priceList.length"
          layout="total, prev, pager, next, jumper"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else-if="selectedItemName && priceList.length === 0" class="empty-state">
      <div class="empty-icon">📊</div>
      <div class="empty-text">未找到该道具在24小时内的价格数据</div>
    </div>
    <div v-else class="empty-state">
      <div class="empty-icon">🔍</div>
      <div class="empty-text">请搜索道具名称查看价格信息</div>
    </div>

    <!-- 历史价格对话框 -->
    <el-dialog
      v-model="historyDialogVisible"
      :title="`${selectedItemName} - ${selectedRegion}`"
      width="90%"
      style="max-width: 900px"
      @close="handleDialogClose"
      class="custom-dialog"
    >
      <div v-loading="historyLoading" class="chart-wrapper">
        <div ref="chartContainer" class="chart-container"></div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, nextTick, computed } from 'vue'
import itemPriceDataAPI from '@/api/itemPriceData'
import { ElMessage } from 'element-plus'
import * as echarts from 'echarts'

export default {
  name: 'ItemPriceStatistics',
  setup() {
    const searchKeyword = ref('')
    const selectedItemName = ref('')
    const priceList = ref([])
    const historyDialogVisible = ref(false)
    const historyLoading = ref(false)
    const selectedRegion = ref('')
    const chartContainer = ref(null)
    const currentPage = ref(1)
    const pageSize = ref(100) // 每页显示100个大区（10行×10列）
    let chartInstance = null
    
    // 计算分页后的价格列表
    const paginatedPriceList = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value
      const end = start + pageSize.value
      return priceList.value.slice(start, end)
    })

    const searchItems = async (queryString, cb) => {
      try {
        // 支持空值搜索，传空字符串获取所有道具
        const keyword = queryString ? queryString.trim() : ''
        const response = await itemPriceDataAPI.searchItems(keyword)
        if (response && response.code === 200) {
          const items = (response.data || []).map(item => ({
            value: item
          }))
          cb(items)
        } else {
          cb([])
        }
      } catch (error) {
        console.error('搜索道具失败:', error)
        cb([])
      }
    }

    const handleItemSelect = async (item) => {
      selectedItemName.value = item.value
      await loadPriceList(item.value)
    }

    const handleItemClear = () => {
      selectedItemName.value = ''
      priceList.value = []
    }

    const loadPriceList = async (itemName) => {
      try {
        const response = await itemPriceDataAPI.getLatestPrices(itemName)
        if (response && response.code === 200) {
          priceList.value = response.data || []
          currentPage.value = 1 // 重置到第一页
        } else {
          ElMessage.error(response?.message || '获取价格列表失败')
          priceList.value = []
        }
      } catch (error) {
        console.error('获取价格列表失败:', error)
        ElMessage.error('获取价格列表失败: ' + (error.response?.data?.message || error.message))
        priceList.value = []
      }
    }
    
    // 分页变化处理
    const handlePageChange = (page) => {
      currentPage.value = page
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const handleRowClick = async (row) => {
      selectedRegion.value = row.region
      historyDialogVisible.value = true
      await loadPriceHistory(row.region)
    }

    const loadPriceHistory = async (region) => {
      try {
        historyLoading.value = true
        const response = await itemPriceDataAPI.getPriceHistory(selectedItemName.value, region)
        if (response && response.code === 200) {
          const history = response.data || []
          await nextTick()
          drawChart(history)
        } else {
          ElMessage.error(response?.message || '获取历史价格失败')
        }
      } catch (error) {
        console.error('获取历史价格失败:', error)
        ElMessage.error('获取历史价格失败: ' + (error.response?.data?.message || error.message))
      } finally {
        historyLoading.value = false
      }
    }

    const drawChart = (history) => {
      if (!chartContainer.value) return

      if (chartInstance) {
        chartInstance.dispose()
      }

      chartInstance = echarts.init(chartContainer.value)

      const dates = history.map(item => {
        if (!item.date) return ''
        const dateStr = typeof item.date === 'string' ? item.date : item.date
        const date = new Date(dateStr)
        if (isNaN(date.getTime())) {
          return dateStr
        }
        return `${date.getMonth() + 1}/${date.getDate()}`
      })
      const prices = history.map(item => {
        const price = item.price
        return typeof price === 'string' ? parseFloat(price) : (price ? parseFloat(price) : 0)
      })

      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e4e7ed',
          borderWidth: 1,
          padding: 12,
          textStyle: {
            color: '#303133',
            fontSize: 13
          },
          formatter: (params) => {
            const param = params[0]
            return `<div style="font-weight: 600; margin-bottom: 4px;">${param.name}</div><div style="color: #6366f1;">价格: ${formatPrice(param.value)}</div>`
          }
        },
        grid: {
          left: '2%',
          right: '2%',
          bottom: '8%',
          top: '12%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLine: {
            lineStyle: {
              color: '#e4e7ed'
            }
          },
          axisLabel: {
            color: '#606266',
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          name: '价格（钻石）',
          nameTextStyle: {
            color: '#909399',
            fontSize: 12
          },
          axisLine: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: '#f2f3f5',
              type: 'dashed'
            }
          },
          axisLabel: {
            color: '#606266',
            fontSize: 12,
            formatter: (value) => formatPrice(value)
          }
        },
        series: [
          {
            name: '价格',
            type: 'line',
            smooth: true,
            data: prices,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              width: 3,
              color: '#6366f1'
            },
            itemStyle: {
              color: '#6366f1',
              borderWidth: 2,
              borderColor: '#fff'
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(99, 102, 241, 0.25)' },
                  { offset: 1, color: 'rgba(99, 102, 241, 0.02)' }
                ]
              }
            }
          }
        ]
      }

      chartInstance.setOption(option)

      window.addEventListener('resize', () => {
        if (chartInstance) {
          chartInstance.resize()
        }
      })
    }

    const formatPrice = (price) => {
      if (!price) return '-'
      const num = typeof price === 'string' ? parseFloat(price) : price
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }

    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-'
      const d = new Date(dateTime)
      return d.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const handleDialogClose = () => {
      if (chartInstance) {
        chartInstance.dispose()
        chartInstance = null
      }
    }

    onMounted(() => {
      // 组件挂载时的初始化
    })

    return {
      searchKeyword,
      selectedItemName,
      priceList,
      paginatedPriceList,
      historyDialogVisible,
      historyLoading,
      selectedRegion,
      chartContainer,
      currentPage,
      pageSize,
      searchItems,
      handleItemSelect,
      handleItemClear,
      handleRowClick,
      handlePageChange,
      handleDialogClose,
      formatPrice,
      formatDateTime
    }
  }
}
</script>

<style scoped>
.item-price-statistics {
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px 40px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.header-section {
  margin-bottom: 40px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.header-content h2 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  letter-spacing: -0.5px;
}

.header-content p {
  margin: 6px 0 0 0;
  color: #6b7280;
  font-size: 15px;
  font-weight: 400;
}

.search-section {
  margin-bottom: 32px;
}

.search-wrapper {
  max-width: 600px;
}

.custom-autocomplete {
  width: 100%;
}

:deep(.custom-autocomplete .el-input__wrapper) {
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04);
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

:deep(.custom-autocomplete .el-input__wrapper:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:deep(.custom-autocomplete .el-input__wrapper.is-focus) {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1), 0 4px 12px rgba(0, 0, 0, 0.08);
}

:deep(.custom-autocomplete .el-input__inner) {
  font-size: 15px;
  color: #1f2937;
}

.autocomplete-item {
  padding: 4px 0;
  font-size: 14px;
  color: #374151;
}

.price-section {
  margin-top: 32px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.item-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 15px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.25);
}

.item-description {
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
}

.price-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 16px;
}

.price-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.price-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.price-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
  border-color: #667eea;
}

.price-card:hover::before {
  transform: scaleX(1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.region-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.arrow-icon {
  color: #9ca3af;
  transition: all 0.25s ease;
}

.price-card:hover .arrow-icon {
  color: #667eea;
  transform: translateX(4px);
}

.price-value {
  font-size: 24px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
  line-height: 1.2;
}

.update-time {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #9ca3af;
  font-size: 11px;
}

.update-time svg {
  flex-shrink: 0;
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.empty-icon {
  font-size: 72px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-text {
  color: #6b7280;
  font-size: 16px;
  font-weight: 500;
}

.pagination-wrapper {
  margin-top: 32px;
  display: flex;
  justify-content: center;
  padding: 24px 0;
}

.chart-wrapper {
  min-height: 400px;
}

.chart-container {
  width: 100%;
  height: 450px;
}

:deep(.custom-dialog .el-dialog__header) {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

:deep(.custom-dialog .el-dialog__title) {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

:deep(.custom-dialog .el-dialog__body) {
  padding: 24px;
}

@media (max-width: 1600px) {
  .price-grid {
    grid-template-columns: repeat(8, 1fr);
  }
}

@media (max-width: 1200px) {
  .price-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

@media (max-width: 768px) {
  .item-price-statistics {
    padding: 20px 16px;
  }

  .header-content h2 {
    font-size: 24px;
  }

  .price-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
  
  .price-card {
    padding: 12px;
    min-height: 120px;
  }
  
  .price-value {
    font-size: 20px;
  }
  
  .region-name {
    font-size: 12px;
  }
  
  .update-time {
    font-size: 10px;
  }

  .chart-container {
    height: 350px;
  }
}
</style>